import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Public /terms (also /terms-and-conditions) — reads the currently published
 * Terms & Conditions record from the API and renders the single-document
 * HTML in the CLAÈ house style. Falls back to a "not yet published" message
 * if no version is live.
 *
 * When invoked with the `preview` prop (from the admin editor) it simply
 * renders whatever payload it's given, without touching the network.
 */
export default function Terms({ preview }) {
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    if (preview) {
      setState({ status: "ok", data: preview });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/terms/published`);
        if (!cancelled) setState({ status: "ok", data: r.data });
      } catch {
        if (!cancelled) setState({ status: "error", data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [preview]);

  useEffect(() => {
    const prev = document.title;
    document.title = "Terms & Conditions · CLAÈ";
    return () => { document.title = prev; };
  }, []);

  const publishedDate = useMemo(() => {
    const iso = state.data?.published_at;
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric", month: "long", year: "numeric",
      });
    } catch { return null; }
  }, [state.data]);

  const title = state.data?.title || "Terms & Conditions";
  const rawHtml = state.data?.content_html || "";
  // Auto-bold "numbered section" paragraphs (e.g. "01 — APPOINTMENTS & ELIGIBILITY",
  // "1. INTRODUCTION", "SECTION 3 — CANCELLATIONS") without touching wording. A
  // paragraph is treated as a section heading when it starts with a digit or
  // the word SECTION and every alphabetic character in it is uppercase.
  const contentHtml = useMemo(() => {
    if (!rawHtml) return "";
    if (typeof window === "undefined" || !window.DOMParser) return rawHtml;
    try {
      const doc = new DOMParser().parseFromString(`<div>${rawHtml}</div>`, "text/html");
      const root = doc.body.firstChild;
      const isHeading = (txt) => {
        const t = (txt || "").trim();
        if (t.length < 3 || t.length > 140) return false;
        if (!/^(\d|SECTION\b)/i.test(t)) return false;
        const letters = t.replace(/[^A-Za-z]/g, "");
        if (letters.length < 2) return false;
        return letters === letters.toUpperCase();
      };
      root.querySelectorAll("p").forEach((p) => {
        if (isHeading(p.textContent)) p.classList.add("terms-section-heading");
      });
      return root.innerHTML;
    } catch {
      return rawHtml;
    }
  }, [rawHtml]);
  const notPublished = state.status === "ok" && !state.data?.published && !preview;
  const emptyDoc = state.status === "ok" && state.data?.published && !contentHtml.trim();

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#FAF9F6", color: "#141414" }}
      data-testid="terms-page"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <header className="mb-12">
          {!preview && (
            <Link
              to="/"
              className="mono-label text-[10px] tracking-[0.24em] text-[#66615C] hover:text-[#141414]"
              data-testid="terms-back"
            >
              ← BACK TO CLAÈ
            </Link>
          )}
          <p className="mono-label text-[11px] tracking-[0.28em] uppercase text-[#66615C] mt-8">
            CLAÈ AESTHETICS
          </p>
          <h1
            className="font-light tracking-[0.02em] text-[38px] sm:text-[48px] leading-tight mt-2"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            data-testid="terms-title"
          >
            {title}
          </h1>
          <div className="mt-4 h-[2px] w-12" style={{ backgroundColor: "#C5A059" }} />
          {(publishedDate || state.data?.version) && (
            <p className="mt-6 text-[12px] text-[#66615C]" data-testid="terms-meta">
              {state.data?.version && <span>Version {state.data.version}</span>}
              {state.data?.version && publishedDate && <span> · </span>}
              {publishedDate && <span>Published {publishedDate}</span>}
            </p>
          )}
        </header>

        {state.status === "loading" && (
          <p className="text-[13px] text-[#66615C]" data-testid="terms-loading">Loading…</p>
        )}

        {state.status === "error" && (
          <p className="text-[13px] text-[#66615C]" data-testid="terms-error">
            We couldn't load the Terms & Conditions. Please try again shortly.
          </p>
        )}

        {notPublished && (
          <p className="text-[13px] text-[#66615C]" data-testid="terms-not-published">
            The CLAÈ Terms & Conditions haven't been published yet. Please check back soon.
          </p>
        )}

        {emptyDoc && (
          <p className="text-[13px] text-[#66615C]" data-testid="terms-empty">
            No content has been added yet.
          </p>
        )}

        {state.status === "ok" && contentHtml && (
          <article
            className="clae-terms-content"
            data-testid="terms-document"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contentHtml) }}
          />
        )}

        {!preview && (
          <footer className="mt-16 pt-8 border-t hairline flex flex-wrap items-center gap-x-6 gap-y-3 justify-between">
            <p className="text-[11px] text-[#66615C]">
              For questions about these terms, please contact CLAÈ directly.
            </p>
            <div className="flex items-center gap-4 mono-label text-[10px] tracking-[0.24em] text-[#66615C]">
              <Link to="/privacy" className="hover:text-[#141414]" data-testid="terms-privacy-link">
                PRIVACY POLICY
              </Link>
              <Link to="/booking" className="hover:text-[#141414]" data-testid="terms-booking-link">
                BOOK
              </Link>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
